import { forwardRef } from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'

const fadeInVariants = tv({
  // `fill-mode-backwards` holds the hidden start-state through `delay` so staggered
  // siblings don't flash visible before their turn.
  base: 'animate-in fade-in fill-mode-backwards ease-out',
  variants: {
    slide: {
      none: '',
      up: 'slide-in-from-bottom-2',
      down: 'slide-in-from-top-2',
      left: 'slide-in-from-right-2',
      right: 'slide-in-from-left-2'
    }
  },
  defaultVariants: { slide: 'up' }
})

export interface FadeInProps
  extends React.ComponentPropsWithRef<'div'>,
    VariantProps<typeof fadeInVariants> {
  /** Animation duration in ms. Default 400. */
  duration?: number
  /** Start delay in ms — use to stagger siblings. Default 0. */
  delay?: number
}

/**
 * Wrapper that fades (and optionally slides) its content in on mount.
 *
 * Honors `prefers-reduced-motion` via {@link useReducedMotion}: when reduced
 * motion is requested, no animation classes or inline animation styles are
 * applied and the content simply appears.
 */
export const FadeIn = forwardRef<HTMLDivElement, FadeInProps>(
  ({ slide, duration = 400, delay = 0, className, style, ...props }, ref) => {
    const reducedMotion = useReducedMotion()

    return (
      <div
        ref={ref}
        data-slot='fade-in'
        className={cn(!reducedMotion && fadeInVariants({ slide }), className)}
        style={
          reducedMotion
            ? style
            : {
                animationDuration: `${duration}ms`,
                animationDelay: `${delay}ms`,
                ...style
              }
        }
        {...props}
      />
    )
  }
)
FadeIn.displayName = 'FadeIn'
